import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../services/api';
import {
  Download, FileSpreadsheet, FileText, Calendar, TrendingUp, TrendingDown,
  Calculator, Users, Loader2, Sparkles, Archive, CheckCircle2, History,
  Banknote, ArrowDown, ArrowUp,
} from 'lucide-react';

const PRESETS = [
  { id: 'q_current', label: 'Trimestre actual' },
  { id: 'q_prev', label: 'Trimestre anterior' },
  { id: 'year_current', label: 'Año en curso' },
  { id: 'year_prev', label: 'Año anterior' },
  { id: 'last_3_months', label: 'Últimos 3 meses' },
  { id: 'custom', label: 'Rango personalizado' },
];

function computePreset(presetId) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const q = Math.ceil(m / 3);
  const pad = (n) => String(n).padStart(2, '0');
  const lastDay = (yy, mm) => new Date(yy, mm, 0).getDate();
  if (presetId === 'q_current') {
    const start = (q - 1) * 3 + 1;
    return { dfrom: `${y}-${pad(start)}-01`, dto: `${y}-${pad(start + 2)}-${lastDay(y, start + 2)}` };
  }
  if (presetId === 'q_prev') {
    const prevQ = q === 1 ? 4 : q - 1;
    const prevY = q === 1 ? y - 1 : y;
    const start = (prevQ - 1) * 3 + 1;
    return { dfrom: `${prevY}-${pad(start)}-01`, dto: `${prevY}-${pad(start + 2)}-${lastDay(prevY, start + 2)}` };
  }
  if (presetId === 'year_current') return { dfrom: `${y}-01-01`, dto: `${y}-12-31` };
  if (presetId === 'year_prev') return { dfrom: `${y - 1}-01-01`, dto: `${y - 1}-12-31` };
  if (presetId === 'last_3_months') {
    const end = today;
    const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    return {
      dfrom: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-01`,
      dto: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${lastDay(end.getFullYear(), end.getMonth() + 1)}`,
    };
  }
  return null;
}

const fmtEur = (n) =>
  (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

const ExportCenterPage = () => {
  const [preset, setPreset] = useState('q_current');
  const initial = computePreset('q_current');
  const [dfrom, setDfrom] = useState(initial.dfrom);
  const [dto, setDto] = useState(initial.dto);
  const [includeInvoices, setIncludeInvoices] = useState(true);
  const [includeExpenses, setIncludeExpenses] = useState(true);
  const [includeClients, setIncludeClients] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [format, setFormat] = useState('excel');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [history, setHistory] = useState([]);

  const onPresetChange = (id) => {
    setPreset(id);
    if (id !== 'custom') {
      const r = computePreset(id);
      if (r) { setDfrom(r.dfrom); setDto(r.dto); }
    }
  };

  const loadSummary = async () => {
    if (!dfrom || !dto) return;
    setLoadingSummary(true);
    try {
      const { data } = await api.get('/export-center/summary', { params: { dfrom, dto } });
      setSummary(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando resumen');
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/export-center/history');
      setHistory(data?.items || []);
    } catch {}
  };

  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [dfrom, dto]);
  useEffect(() => { loadHistory(); }, []);

  const generatePack = async () => {
    setDownloading(true);
    try {
      const res = await api.post('/export-center/pack', {
        dfrom, dto, format,
        include_invoices: includeInvoices,
        include_expenses: includeExpenses,
        include_clients: includeClients,
        include_summary_303: includeSummary,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'zip';
      a.download = `facturcal_export_${dfrom}_${dto}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Exportación generada');
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error generando el paquete');
    } finally {
      setDownloading(false);
    }
  };

  const t = summary?.totals;
  const c = summary?.counts;

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="export-center-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900 flex items-center gap-2 font-heading">
            <Archive className="w-7 h-7 text-violet-500" />
            Centro de Exportación
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Genera paquetes consolidados para tu gestor en un solo click. Excel multi-hoja o ZIP con CSVs.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT: Config panel */}
        <div className="lg:col-span-1 space-y-4">
          {/* Period preset */}
          <div className="bg-white border border-stone-100 rounded-2xl p-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> Periodo
            </h2>
            <Select value={preset} onValueChange={onPresetChange}>
              <SelectTrigger className="rounded-xl h-11" data-testid="period-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div>
                <Label className="text-xs text-stone-500">Desde</Label>
                <Input type="date" value={dfrom}
                  onChange={(e) => { setPreset('custom'); setDfrom(e.target.value); }}
                  className="rounded-xl mt-1" data-testid="date-from" />
              </div>
              <div>
                <Label className="text-xs text-stone-500">Hasta</Label>
                <Input type="date" value={dto}
                  onChange={(e) => { setPreset('custom'); setDto(e.target.value); }}
                  className="rounded-xl mt-1" data-testid="date-to" />
              </div>
            </div>
          </div>

          {/* Includes */}
          <div className="bg-white border border-stone-100 rounded-2xl p-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Contenido del paquete
            </h2>
            <div className="space-y-3">
              {[
                { id: 'summary', label: 'Resumen Modelo 303', icon: Calculator, state: includeSummary, set: setIncludeSummary, testId: 'inc-summary' },
                { id: 'invoices', label: 'Facturas emitidas', icon: FileText, state: includeInvoices, set: setIncludeInvoices, testId: 'inc-invoices' },
                { id: 'expenses', label: 'Gastos / IVA soportado', icon: TrendingDown, state: includeExpenses, set: setIncludeExpenses, testId: 'inc-expenses' },
                { id: 'clients', label: 'Clientes facturados', icon: Users, state: includeClients, set: setIncludeClients, testId: 'inc-clients' },
              ].map((row) => {
                const Icon = row.icon;
                return (
                  <div key={row.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-stone-500" />
                      <Label className="text-sm text-stone-800">{row.label}</Label>
                    </div>
                    <Switch checked={row.state} onCheckedChange={row.set} data-testid={row.testId} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Format + Download */}
          <div className="bg-white border border-stone-100 rounded-2xl p-5">
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-1.5">
              <FileSpreadsheet className="w-4 h-4" /> Formato
            </h2>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setFormat('excel')}
                className={`p-3 rounded-xl border-2 transition-all text-left ${format === 'excel' ? 'border-violet-400 bg-violet-50' : 'border-stone-200 hover:border-stone-300'}`}
                data-testid="format-excel"
              >
                <FileSpreadsheet className={`w-5 h-5 mb-1 ${format === 'excel' ? 'text-violet-600' : 'text-stone-500'}`} />
                <p className="text-xs font-bold text-stone-800">Excel</p>
                <p className="text-[10px] text-stone-500">multi-hoja</p>
              </button>
              <button
                onClick={() => setFormat('csv')}
                className={`p-3 rounded-xl border-2 transition-all text-left ${format === 'csv' ? 'border-violet-400 bg-violet-50' : 'border-stone-200 hover:border-stone-300'}`}
                data-testid="format-csv"
              >
                <Archive className={`w-5 h-5 mb-1 ${format === 'csv' ? 'text-violet-600' : 'text-stone-500'}`} />
                <p className="text-xs font-bold text-stone-800">ZIP de CSVs</p>
                <p className="text-[10px] text-stone-500">universal</p>
              </button>
            </div>

            <Button
              onClick={generatePack}
              disabled={downloading || !dfrom || !dto || (!includeInvoices && !includeExpenses && !includeClients && !includeSummary)}
              className="w-full rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 text-white shadow-md shadow-violet-200/50 h-11 font-bold"
              data-testid="generate-pack-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {downloading ? 'Generando…' : 'Generar paquete'}
            </Button>
          </div>
        </div>

        {/* RIGHT: Summary preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-stone-100 rounded-2xl p-6" data-testid="summary-preview">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-extrabold font-heading">Vista previa del periodo</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  {dfrom} → {dto}
                </p>
              </div>
              {loadingSummary && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
            </div>

            {summary && t && c && (
              <>
                {/* Counts */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                  <StatBlock icon={FileText} color="violet" label="Facturas" value={c.invoices_active} sub={`+${c.invoices_draft} borrador(es)`} />
                  <StatBlock icon={TrendingDown} color="orange" label="Gastos" value={c.expenses} sub="con IVA deducible" />
                  <StatBlock icon={Users} color="emerald" label="Clientes" value={c.clients_with_invoices} sub="únicos facturados" />
                  <StatBlock icon={Calculator} color="rose" label="A ingresar 303" value={fmtEur(t.iva_a_ingresar_303)} sub="rep − sop" isCurrency />
                </div>

                {/* IVA breakdown */}
                <div className="bg-stone-50 rounded-2xl p-5">
                  <h3 className="text-xs font-extrabold uppercase tracking-widest text-stone-500 mb-4">Resumen del IVA</h3>
                  <div className="space-y-3 text-sm">
                    <Line label="Base imponible repercutida" icon={ArrowUp} value={t.base_repercutida} positive />
                    <Line label="IVA repercutido (cobrado)" icon={ArrowUp} value={t.iva_repercutido} positive bold />
                    <hr className="border-stone-200" />
                    <Line label="Base imponible soportada" icon={ArrowDown} value={t.base_soportada} negative />
                    <Line label="IVA soportado (pagado)" icon={ArrowDown} value={t.iva_soportado} negative bold />
                    <hr className="border-stone-200" />
                    <Line label="IVA a ingresar (Modelo 303)" icon={Banknote} value={t.iva_a_ingresar_303} bold highlight />
                    {t.irpf_retenido > 0 && (
                      <>
                        <hr className="border-stone-200" />
                        <Line label="IRPF retenido por tus clientes" value={t.irpf_retenido} />
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {!loadingSummary && !summary && (
              <p className="text-sm text-stone-500 text-center py-8">Sin datos para ese rango.</p>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-2xl p-6" data-testid="export-history">
              <h2 className="text-base font-extrabold font-heading flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-stone-500" />
                Historial de exportaciones
              </h2>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {history.map((h) => (
                  <div key={h._id} className="flex items-center justify-between text-sm p-3 rounded-xl bg-stone-50">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-stone-700 truncate">{h.filename}</p>
                      <p className="text-[10px] text-stone-500">
                        {new Date(h.created_at).toLocaleString('es-ES')} · {(h.size_bytes / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-3" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const COLOR_MAP = {
  violet: 'from-violet-500 to-fuchsia-500',
  orange: 'from-orange-500 to-rose-500',
  emerald: 'from-emerald-500 to-teal-500',
  rose: 'from-rose-500 to-red-500',
};

const StatBlock = ({ icon: Icon, color, label, value, sub, isCurrency }) => (
  <div className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${COLOR_MAP[color]} text-white`}>
    <Icon className="w-4 h-4 opacity-70 mb-2" />
    <p className={`font-extrabold leading-tight ${isCurrency ? 'text-lg' : 'text-2xl'}`}>{value}</p>
    <p className="text-[10px] uppercase tracking-widest font-bold opacity-80 mt-1">{label}</p>
    {sub && <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>}
  </div>
);

const Line = ({ label, icon: Icon, value, positive, negative, bold, highlight }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      {Icon && <Icon className={`w-3.5 h-3.5 ${positive ? 'text-emerald-500' : negative ? 'text-rose-500' : 'text-violet-500'}`} />}
      <span className={`${bold ? 'font-bold' : 'font-medium'} ${highlight ? 'text-violet-900 text-base' : 'text-stone-700'}`}>{label}</span>
    </div>
    <span className={`${bold ? 'font-extrabold' : 'font-bold'} tabular-nums ${highlight ? 'text-violet-700 text-base' : 'text-stone-900'}`}>
      {fmtEur(value)}
    </span>
  </div>
);

export default ExportCenterPage;
